import { defineComponent as _defineComponent } from 'vue'
import { storeToRefs } from 'pinia'
import { computed, onUnmounted, provide, ref } from 'vue'
import { useWindowSize } from '@vueuse/core'
import { SetRequired } from 'type-fest'

import TransitForm from '../forms/TransitForm.vue'
import FlightForm from '../forms/FlightForm.vue'
import WalkingForm from '../forms/WalkingForm.vue'
import TransportationSlideOverMap from '../slideOver/TransportationSlideOverMap.vue'
import { validationErrorsSymbol } from '../utils'
import DrivingForm from './DrivingForm.vue'
import CyclingForm from './CyclingForm.vue'
import BoatForm from './BoatForm.vue'
import DeleteActions from './DeleteActions.vue'
import TransportationSelector from '@/components/trip/transportations/TransportationSelector.vue'
import DeleteModal from '@/components/core/DeleteModal.vue'
import LButton from '@/components/core/Button.vue'
import { useContext } from '@/composables/context'
import { useSelectedTrip } from '@/composables/trip'
import { useTransportationValidation } from '@/composables/transportation-validation'
import {
  useCreateTransportation,
  useUpdateTransportation,
} from '@/composables/transportation'
import { useTransportationGroupSync } from '@/composables/transportation-group-sync'
import { eventModule, tripModule } from '@/store'
import { useTransportationStore } from '@/stores/transportation'
import {
  TransportationUIInput,
  TransportationCreateInput,
  TransportationUpdateInput,
  isPersistedUITransportation,
} from '@/types/transportation'


export default /*#__PURE__*/_defineComponent({
  __name: 'TransportationForm',
  emits: ["close"],
  setup(__props, { emit }: { emit: ({
  (e: 'close'): void
}), expose: any, slots: any, attrs: any }) {



const { $sentry } = useContext()
const trip = useSelectedTrip()
const transportationStore = useTransportationStore()
const { selectedTransportation, isSaveDisabled } =
  storeToRefs(transportationStore)

// Validation Management -------------------------------------------------------
const { errors, isValidating, validateTransportation } =
  useTransportationValidation()

// Provide validation state to child components
provide(validationErrorsSymbol, errors)

const transportation = computed(() => {
  return selectedTransportation.value?.transportation
})

/**
 * Waypoint Id where the transportation should be added/updated
 */
const waypointId = computed(() => {
  return selectedTransportation.value?.waypointId
})

/**
 * Index of where the new transportation should be added in the list
 */
const index = computed(() => {
  return selectedTransportation.value?.index
})

const isLoading = ref(false)
const showAutoDemoteModal = ref(false)

const { mutateAsync: createMutation } = useCreateTransportation(trip.value._id)
const { mutateAsync: updateMutation } = useUpdateTransportation(trip.value._id)
const drivingFormRef = ref<InstanceType<typeof DrivingForm> | null>(null)

const { currentGroup, diffSelection, syncGroupSelection } =
  useTransportationGroupSync({ transportation, waypointId })

async function persistTransportation(
  transportationToPersist: SetRequired<
    TransportationUIInput,
    'origin' | 'destination'
  >,
) {
  if (transportationToPersist.type === 'flight') {
    transportationToPersist.arrivalAirport.iata =
      transportationToPersist.arrivalAirport.iata?.trim()
    transportationToPersist.departureAirport.iata =
      transportationToPersist.departureAirport.iata?.trim()

    if (transportationToPersist.arrivalAirport.iata?.length === 0) {
      transportationToPersist.arrivalAirport.iata = undefined
    }
    if (transportationToPersist.departureAirport.iata?.length === 0) {
      transportationToPersist.departureAirport.iata = undefined
    }

    transportationToPersist.flightNumber = transportationToPersist.flightNumber
      ?.trim()
      .toUpperCase()
  }

  if (isPersistedUITransportation(transportationToPersist)) {
    await updateTransportation(transportationToPersist)
    return transportationToPersist._id
  }
  const created = await createTransportation(transportationToPersist)
  if (created?._id) {
    transportationToPersist._id = created._id
  }
  return created?._id ?? null
}

async function saveAndClose() {
  const diff = diffSelection()
  if (diff.willDemote) {
    showAutoDemoteModal.value = true
    return
  }
  await commitSave()
}

async function commitSave() {
  if (!transportation.value) {
    return
  }
  const validationResult = validateTransportation(transportation.value)
  if (!validationResult) {
    return
  }
  if (
    transportation.value.type === 'driving' &&
    (await drivingFormRef.value?.validateLegPickerAnchors()) === false
  ) {
    return
  }
  const isUpdate = isPersistedUITransportation(transportation.value)
  isLoading.value = true
  try {
    const id = await persistTransportation(transportation.value)
    if (!id) {
      return
    }
    await syncGroupSelection(id)
  } catch (error) {
    // The base transportation create/update mutations surface their own
    // error toast via per-call onError (no Sentry capture); the group
    // mutations additionally capture to Sentry. Capture here so the core
    // save path has Sentry observability too — both for those toast-only
    // mutation errors and for any unexpected error in the save flow. Keep
    // the modal open so the user can retry instead of showing success for a
    // partially applied save. Retry is safe: the created _id is copied onto
    // the draft, so a re-submit becomes an update, not a duplicate.
    $sentry.captureException(error)
    return
  } finally {
    isLoading.value = false
  }
  eventModule.newMessage(
    isUpdate
      ? 'success.transportation.update'
      : 'success.transportation.create',
  )
  emit('close')
  // Post-save UI refresh: tripModule.fetch self-catches (error.trip.fetch) and
  // never throws, so it cannot suppress the success path. vue-query
  // invalidation in the mutations already refreshes the modern data layer.
  await tripModule.fetch(trip.value._id)
}

async function confirmAutoDemote() {
  showAutoDemoteModal.value = false
  await commitSave()
}

async function createTransportation(
  transportation: SetRequired<TransportationUIInput, 'origin' | 'destination'>,
) {
  if (!waypointId.value) {
    return null
  }

  const createData = {
    ...transportation,
    origin: transportation.origin.location,
    destination: transportation.destination.location,
  } satisfies TransportationCreateInput

  return await createMutation(
    {
      transportation: createData,
      waypointId: waypointId.value,
      index: index.value,
    },
    {
      onError: () => {
        eventModule.newError('error.transportation.create')
      },
    },
  )
}

async function updateTransportation(
  transportation: SetRequired<
    TransportationUIInput,
    '_id' | 'origin' | 'destination'
  >,
) {
  if (!waypointId.value) {
    return
  }

  const oldTransportation = transportationStore.getTransportationById(
    transportation._id,
  )
  if (!oldTransportation) {
    eventModule.newError('error.transportation.update')
    throw new Error('Transportation to update not found in store')
  }

  // only fetch details if the locations have changed
  const origin =
    oldTransportation.origin.placeId !== transportation.origin.id
      ? transportation.origin.location
      : undefined
  const destination =
    oldTransportation.destination.placeId !== transportation.destination.id
      ? transportation.destination.location
      : undefined

  const route =
    oldTransportation.route && !transportation.route
      ? null
      : transportation.route

  // TODO: Improve to only send changed fields
  const updateData = {
    ...transportation,
    origin,
    destination,
    route,
  } satisfies TransportationUpdateInput

  await updateMutation(
    {
      transportation: updateData,
      transportationId: transportation._id,
      waypointId: waypointId.value,
      index: index.value,
    },
    {
      onError: () => {
        eventModule.newError('error.transportation.update')
      },
    },
  )
}

// Handle mobile map visibility ------------------------------------------------

const { width } = useWindowSize()

const showMobileMap = computed(() => {
  return width.value <= 759
})

// Ensure store is always properly cleared -------------------------------------

onUnmounted(() => {
  transportationStore.clearSelectedTransportation()
})

return { __sfc: true,emit, $sentry, trip, transportationStore, selectedTransportation, isSaveDisabled, errors, isValidating, validateTransportation, transportation, waypointId, index, isLoading, showAutoDemoteModal, createMutation, updateMutation, drivingFormRef, currentGroup, diffSelection, syncGroupSelection, persistTransportation, saveAndClose, commitSave, confirmAutoDemote, createTransportation, updateTransportation, width, showMobileMap, TransitForm, FlightForm, WalkingForm, TransportationSlideOverMap, DrivingForm, CyclingForm, BoatForm, DeleteActions, TransportationSelector, DeleteModal, LButton, isPersistedUITransportation }
}

})